import { type FC, useState, useMemo, useCallback, useEffect, useRef } from "react";
import clsx from "clsx";
import PageHeader from "../components/PageHeader";
import { TabBar } from "../components/molecules/TabBar";
import { Button } from "../components/atoms";
import { useMenuTitle } from "../shared/menu/useMenuTitle";
import {
    fetchManHourRequired,
    bulkSaveManHourRequired,
    type ManHourRequiredResponse,
} from "../api/manHourRequired";

// ── 定数 ──────────────────────────────────────
const ALL_HOURS = Array.from({ length: 24 }, (_, i) => i); // 0..23
const HOUR_OPTIONS = Array.from({ length: 25 }, (_, i) => i); // 0..24（終了用に24も含む）
const SLOTS_PER_HOUR = 12; // 5分刻み
const TOTAL_SLOTS = 24 * SLOTS_PER_HOUR; // 288（ヘッドカウント計算用）
const SLOT_W = 18; // 各スロットの幅 (px)
const LEFT_COL_W = 120; // 時間帯列
const HOUR_COL_W = 50; // 合計時間列
const DEFAULT_MIN_DISPLAY_ROWS = 5; // 初期表示行数のフォールバック（測定前に使用）

const DAY_TABS = ["日", "月", "火", "水", "木", "金", "土", "祝"] as const;
type DayTab = (typeof DAY_TABS)[number];

/** 曜日タブ → weekday数値 (0=月〜6=日, 9=祝) */
const DAY_TAB_TO_WEEKDAY: Record<DayTab, number> = {
    "月": 0, "火": 1, "水": 2, "木": 3, "金": 4, "土": 5, "日": 6, "祝": 9,
};

// TODO: β版ではハードコード。組織選択UIが実装されたら動的に取得する
const BETA_ORGANIZATION_ID = 1;

// ── 行データ型 ────────────────────────────────
interface TimelineRow {
    id: number;
    startHour: number | null;
    startMin: number | null;
    endHour: number | null;
    endMin: number | null;
}

interface DragState {
    rowId: number;
    anchorSlot: number;
    currentSlot: number;
}

const isFilledRow = (row: TimelineRow): boolean =>
    row.startHour !== null && row.startMin !== null && row.endHour !== null && row.endMin !== null;

/** スロットインデックス → 時・分 に変換 */
const fromSlot = (slot: number): { hour: number; min: number } => ({
    hour: Math.floor(slot / SLOTS_PER_HOUR),
    min: (slot % SLOTS_PER_HOUR) * 5,
});

let nextId = 100;
const newEmptyRow = (): TimelineRow => ({
    id: nextId++,
    startHour: null,
    startMin: null,
    endHour: null,
    endMin: null,
});

/** 現在の行配列を最低min行まで空行で埋める */
const padRowsToMin = (rows: TimelineRow[], min: number): TimelineRow[] => {
    if (rows.length >= min) return rows;
    const padded = [...rows];
    while (padded.length < min) padded.push(newEmptyRow());
    return padded;
};

// ── API ↔ UI 変換 ─────────────────────────────
/** "HH:MM:SS" → {hour, min} */
const parseTime = (t: string): { hour: number; min: number } => {
    const [h, m] = t.split(":").map(Number);
    return { hour: h, min: m };
};

/** ManHourRequiredResponse → TimelineRow */
const apiToRow = (m: ManHourRequiredResponse): TimelineRow => {
    const from = parseTime(m.start_time);
    const to = parseTime(m.end_time);
    return { id: m.id, startHour: from.hour, startMin: from.min, endHour: to.hour, endMin: to.min };
};

/** TimelineRow → API用 start_time/end_time */
const rowToTimeStrings = (row: TimelineRow): { start_time: string; end_time: string } => {
    const pad = (n: number) => String(n).padStart(2, "0");
    return {
        start_time: `${pad(row.startHour ?? 0)}:${pad(row.startMin ?? 0)}:00`,
        end_time: `${pad(row.endHour ?? 0)}:${pad(row.endMin ?? 0)}:00`,
    };
};

// ── ユーティリティ ────────────────────────────
const pad2 = (n: number): string => String(n).padStart(2, "0");
const formatTime = (h: number, m: number): string => `${h}:${pad2(m)}`;

const calcTotalHours = (row: TimelineRow): string => {
    const total =
        (row.endHour ?? 0) + (row.endMin ?? 0) / 60 - ((row.startHour ?? 0) + (row.startMin ?? 0) / 60);
    return total % 1 === 0 ? `${total}h` : `${total.toFixed(1)}h`;
};

/** 5分単位のスロットインデックス (0..287) */
const toSlot5 = (h: number, m: number): number => h * SLOTS_PER_HOUR + Math.floor(m / 5);

// ── 必要人数の計算（5分スロット単位） ─────────
const useHeadcount = (rows: TimelineRow[]) =>
    useMemo(() => {
        const slots = new Array<number>(TOTAL_SLOTS).fill(0);
        for (const r of rows) {
            if (!isFilledRow(r)) continue;
            const s = toSlot5(r.startHour ?? 0, r.startMin ?? 0);
            const e = toSlot5(r.endHour ?? 0, r.endMin ?? 0);
            for (let i = s; i < e; i++) slots[i]++;
        }
        return slots;
    }, [rows]);

// ── タイムラインセル群（ドラッグ対応） ────────
interface TimelineCellsProps {
    row: TimelineRow;
    slotStart: number;
    slotEnd: number;
    drag: DragState | null;
    onCellMouseDown: (rowId: number, slot: number) => void;
    onCellMouseEnter: (slot: number) => void;
}

const TimelineCells: FC<TimelineCellsProps> = ({ row, slotStart, slotEnd, drag, onCellMouseDown, onCellMouseEnter }) => {
    const rowStart = isFilledRow(row) ? toSlot5(row.startHour ?? 0, row.startMin ?? 0) : -1;
    const rowEnd = isFilledRow(row) ? toSlot5(row.endHour ?? 0, row.endMin ?? 0) : -1;

    const isDraggingThisRow = drag?.rowId === row.id;
    const previewStart = isDraggingThisRow ? Math.min(drag.anchorSlot, drag.currentSlot) : -1;
    const previewEnd = isDraggingThisRow ? Math.max(drag.anchorSlot, drag.currentSlot) + 1 : -1;

    return (
        <>
            {Array.from({ length: slotEnd - slotStart }, (_, idx) => {
                const i = slotStart + idx;
                const isHourBoundary = i % SLOTS_PER_HOUR === 0;
                const active = isDraggingThisRow
                    ? i >= previewStart && i < previewEnd
                    : i >= rowStart && i < rowEnd;
                return (
                    <td
                        key={i}
                        className={clsx(
                            "p-0 h-[34px] select-none cursor-crosshair border-b border-b-[#D9D9D9] border-l border-[#D9D9D9]",
                            active && (isDraggingThisRow ? "bg-brand-200/60" : "bg-brand-300/60"),
                        )}
                        style={{ width: SLOT_W, minWidth: SLOT_W, ...(!isHourBoundary ? { borderLeftStyle: "dashed" as const } : {}) }}
                        onMouseDown={(e) => { e.preventDefault(); onCellMouseDown(row.id, i); }}
                        onMouseEnter={() => { if (drag) onCellMouseEnter(i); }}
                    />
                );
            })}
        </>
    );
};

// ── メインページ ─────────────────────────────
const RequiredManHoursPage = () => {
    const menuTitle = useMenuTitle();
    const [activeTab, setActiveTab] = useState<DayTab>("日");
    // 曜日ごとに行データをキャッシュ（タブ切替で保持される）
    const [rowsByWeekday, setRowsByWeekday] = useState<Record<number, TimelineRow[]>>({});
    const [loadedWeekdays, setLoadedWeekdays] = useState<Set<number>>(() => new Set());
    const [dirtyWeekdays, setDirtyWeekdays] = useState<Set<number>>(() => new Set());
    const [rangeStart, setRangeStart] = useState(8);
    const [rangeEnd, setRangeEnd] = useState(24);
    const [drag, setDrag] = useState<DragState | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [minDisplayRows, setMinDisplayRows] = useState(DEFAULT_MIN_DISPLAY_ROWS);
    const dragRef = useRef<DragState | null>(null);
    dragRef.current = drag;
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const theadRef = useRef<HTMLTableSectionElement>(null);
    const tbodyRef = useRef<HTMLTableSectionElement>(null);

    const currentWeekday = DAY_TAB_TO_WEEKDAY[activeTab];
    const rows = useMemo(
        () => rowsByWeekday[currentWeekday] ?? padRowsToMin([], minDisplayRows),
        [rowsByWeekday, currentWeekday, minDisplayRows],
    );
    const isDirty = dirtyWeekdays.size > 0;

    const headcount = useHeadcount(rows);

    /** 現在の曜日の行データを更新するヘルパー */
    const updateCurrentRows = useCallback(
        (updater: (prev: TimelineRow[]) => TimelineRow[]) => {
            setRowsByWeekday((prev) => {
                const current = prev[currentWeekday] ?? padRowsToMin([], minDisplayRows);
                return { ...prev, [currentWeekday]: updater(current) };
            });
            setDirtyWeekdays((prev) => {
                if (prev.has(currentWeekday)) return prev;
                const next = new Set(prev);
                next.add(currentWeekday);
                return next;
            });
        },
        [currentWeekday, minDisplayRows],
    );

    // ── データ取得 ───────────────────────────
    const loadData = useCallback(async (weekday: number) => {
        setLoading(true);
        try {
            const res = await fetchManHourRequired({
                weekday,
                organization_id: BETA_ORGANIZATION_ID,
            });
            const loaded = padRowsToMin(res.items.map(apiToRow), minDisplayRows);
            setRowsByWeekday((prev) => ({ ...prev, [weekday]: loaded }));
            setLoadedWeekdays((prev) => {
                const next = new Set(prev);
                next.add(weekday);
                return next;
            });
        } catch (e) {
            console.error("Failed to load man_hour_required:", e);
            setRowsByWeekday((prev) => ({ ...prev, [weekday]: padRowsToMin([], minDisplayRows) }));
            setLoadedWeekdays((prev) => {
                const next = new Set(prev);
                next.add(weekday);
                return next;
            });
        } finally {
            setLoading(false);
        }
    }, [minDisplayRows]);

    // タブ切替時、まだ取得していない曜日のみfetch
    useEffect(() => {
        if (loadedWeekdays.has(currentWeekday)) return;
        loadData(currentWeekday);
    }, [currentWeekday, loadedWeekdays, loadData]);

    // ── データ保存（dirtyな曜日をまとめて保存） ─────
    const handleSave = useCallback(async () => {
        if (dirtyWeekdays.size === 0) return;
        setSaving(true);
        try {
            const targets = Array.from(dirtyWeekdays);
            const results = await Promise.all(
                targets.map(async (weekday) => {
                    const target = rowsByWeekday[weekday] ?? [];
                    const filledRows = target.filter(isFilledRow);
                    const res = await bulkSaveManHourRequired({
                        organization_id: BETA_ORGANIZATION_ID,
                        weekday,
                        rows: filledRows.map((r, idx) => ({
                            row_no: idx + 1,
                            ...rowToTimeStrings(r),
                        })),
                    });
                    const serverRows = res.items.map(apiToRow);
                    const emptyRows = target.filter((r) => !isFilledRow(r));
                    return { weekday, rows: padRowsToMin([...serverRows, ...emptyRows], minDisplayRows) };
                }),
            );
            setRowsByWeekday((prev) => {
                const next = { ...prev };
                for (const r of results) next[r.weekday] = r.rows;
                return next;
            });
            setDirtyWeekdays(new Set());
        } catch (e) {
            console.error("Failed to save man_hour_required:", e);
        } finally {
            setSaving(false);
        }
    }, [dirtyWeekdays, rowsByWeekday, minDisplayRows]);

    // ── キャンセル（現在表示中の曜日の未登録の入力のみ破棄してサーバー状態に戻す） ───
    const handleCancel = useCallback(() => {
        if (!dirtyWeekdays.has(currentWeekday)) return;
        setRowsByWeekday((prev) => {
            const next = { ...prev };
            delete next[currentWeekday];
            return next;
        });
        setLoadedWeekdays((prev) => {
            if (!prev.has(currentWeekday)) return prev;
            const next = new Set(prev);
            next.delete(currentWeekday);
            return next;
        });
        setDirtyWeekdays((prev) => {
            if (!prev.has(currentWeekday)) return prev;
            const next = new Set(prev);
            next.delete(currentWeekday);
            return next;
        });
    }, [currentWeekday, dirtyWeekdays]);

    // 合計人時（全行の勤務時間の合計）
    const totalManHours = useMemo(() => {
        let sum = 0;
        for (const r of rows) {
            if (!isFilledRow(r)) continue;
            sum += (r.endHour ?? 0) + (r.endMin ?? 0) / 60 - ((r.startHour ?? 0) + (r.startMin ?? 0) / 60);
        }
        return sum;
    }, [rows]);

    const formatManHours = (v: number): string =>
        v % 1 === 0 ? `${v}h` : `${v.toFixed(1)}h`;

    // 表示範囲の算出
    const visibleHours = useMemo(() => ALL_HOURS.filter((h) => h >= rangeStart && h < rangeEnd), [rangeStart, rangeEnd]);
    const visibleSlotStart = rangeStart * SLOTS_PER_HOUR;
    const visibleSlotEnd = rangeEnd * SLOTS_PER_HOUR;
    const visibleSlotCount = visibleSlotEnd - visibleSlotStart;

    const handleTabClick = useCallback((tab: DayTab) => {
        setActiveTab(tab);
    }, []);

    const handleAddRow = useCallback(() => {
        updateCurrentRows((prev) => [...prev, newEmptyRow()]);
    }, [updateCurrentRows]);

    // ── ドラッグ操作 ─────────────────────────
    const handleCellMouseDown = useCallback((rowId: number, slot: number) => {
        const next: DragState = { rowId, anchorSlot: slot, currentSlot: slot };
        dragRef.current = next;
        setDrag(next);
    }, []);

    const handleCellMouseEnter = useCallback((slot: number) => {
        setDrag((prev) => {
            if (!prev) return null;
            const next = { ...prev, currentSlot: slot };
            dragRef.current = next;
            return next;
        });
    }, []);

    // スクロールコンテナに収まる最大行数を測定し、minDisplayRowsに反映
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const recalc = () => {
            const thead = theadRef.current;
            const tbody = tbodyRef.current;
            if (!thead || !tbody) return;
            const rowCount = tbody.children.length;
            if (rowCount <= 0) return;
            const containerH = container.clientHeight;
            const headH = thead.getBoundingClientRect().height;
            // 複数行の総高さから平均行高を算出（サブピクセルの丸め誤差を抑える）
            const avgRowH = tbody.getBoundingClientRect().height / rowCount;
            if (avgRowH <= 0 || containerH <= 0) return;
            // サブピクセル丸めでギリギリ溢れる場合があるので 1 行安全マージン
            const raw = Math.floor((containerH - headH - 1) / avgRowH);
            const fit = Math.max(1, raw - 1);
            setMinDisplayRows((prev) => (prev === fit ? prev : fit));
        };

        recalc();
        const ro = new ResizeObserver(recalc);
        ro.observe(container);
        return () => ro.disconnect();
    }, [loading]);

    // minDisplayRowsが増えた場合、既存曜日の行を空行で追い埋めする
    useEffect(() => {
        setRowsByWeekday((prev) => {
            let changed = false;
            const next: Record<number, TimelineRow[]> = {};
            for (const key of Object.keys(prev)) {
                const k = Number(key);
                const arr = prev[k];
                if (arr.length < minDisplayRows) {
                    const padded = [...arr];
                    while (padded.length < minDisplayRows) padded.push(newEmptyRow());
                    next[k] = padded;
                    changed = true;
                } else {
                    next[k] = arr;
                }
            }
            return changed ? next : prev;
        });
    }, [minDisplayRows]);

    // mouseup をウィンドウレベルで監視（ドラッグ確定 → 行データを更新）
    useEffect(() => {
        const handleMouseUp = () => {
            const d = dragRef.current;
            if (!d) return;

            const startSlot = Math.min(d.anchorSlot, d.currentSlot);
            const endSlot = Math.max(d.anchorSlot, d.currentSlot) + 1;
            const start = fromSlot(startSlot);
            const end = fromSlot(endSlot);

            updateCurrentRows((prev) =>
                prev.map((r) =>
                    r.id === d.rowId
                        ? { ...r, startHour: start.hour, startMin: start.min, endHour: end.hour, endMin: end.min }
                        : r,
                ),
            );

            dragRef.current = null;
            setDrag(null);
        };

        window.addEventListener("mouseup", handleMouseUp);
        return () => window.removeEventListener("mouseup", handleMouseUp);
    }, [updateCurrentRows]);

    return (
            <div className="flex-1 flex flex-col overflow-hidden">
                <PageHeader title={menuTitle} />
                <TabBar />

                <div className="flex-1 min-h-0 flex flex-col px-6 pb-6">
                    {/* 曜日タブ + 表示範囲セレクト */}
                    <div className="flex items-center gap-0.5">
                        {DAY_TABS.map((tab) => {
                            const tabWeekday = DAY_TAB_TO_WEEKDAY[tab];
                            const tabDirty = dirtyWeekdays.has(tabWeekday);
                            return (
                                <button
                                    key={tab}
                                    type="button"
                                    onClick={() => handleTabClick(tab)}
                                    className={clsx(
                                        "relative px-4 py-2.5 text-sm font-medium rounded-t-md border border-b-0 transition-colors",
                                        activeTab === tab
                                            ? "bg-brand-500 text-white border-brand-500"
                                            : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50",
                                    )}
                                >
                                    {tab}
                                    {tabDirty && (
                                        <span
                                            className={clsx(
                                                "absolute top-1 right-1 w-1.5 h-1.5 rounded-full",
                                                activeTab === tab ? "bg-white" : "bg-brand-500",
                                            )}
                                        />
                                    )}
                                </button>
                            );
                        })}

                        <span className="ml-3 text-sm text-gray-700 font-medium">
                            合計 {formatManHours(totalManHours)}
                        </span>

                        <div className="ml-auto flex items-center gap-2 text-sm text-gray-700">
                            <span>表示範囲</span>
                            <select
                                value={rangeStart}
                                onChange={(e) => {
                                    const v = Number(e.target.value);
                                    setRangeStart(v);
                                    if (v >= rangeEnd) setRangeEnd(Math.min(v + 1, 24));
                                }}
                                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                            >
                                {HOUR_OPTIONS.filter((h) => h < 24).map((h) => (
                                    <option key={h} value={h}>{h}時</option>
                                ))}
                            </select>
                            <span>〜</span>
                            <select
                                value={rangeEnd}
                                onChange={(e) => {
                                    const v = Number(e.target.value);
                                    setRangeEnd(v);
                                    if (v <= rangeStart) setRangeStart(Math.max(v - 1, 0));
                                }}
                                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                            >
                                {HOUR_OPTIONS.filter((h) => h >= 1).map((h) => (
                                    <option key={h} value={h}>{h}時</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* テーブル */}
                    <div className={clsx("border border-[#D9D9D9] rounded-tr-lg rounded-b-lg overflow-hidden flex-1 min-h-0 bg-white max-w-full", !loading && "w-fit")}>
                        <div ref={scrollContainerRef} className="overflow-auto h-full scrollbar-thin">
                            {loading ? (
                                <div className="flex items-center justify-center py-12 text-sm text-gray-400">読み込み中...</div>
                            ) : (
                            <table className="text-sm text-[#1A1A1A] border-separate border-spacing-0">

                                <thead ref={theadRef} className="sticky top-0 z-20">
                                    {/* ヘッダー①: 必要人数（5分スロット単位） */}
                                    <tr style={{ backgroundColor: "#E9F1F2" }}>
                                        <th
                                            className="border-b border-r border-[#D9D9D9] px-2 py-1 text-center font-medium text-[#1A1A1A] whitespace-nowrap sticky left-0 z-30"
                                            style={{ backgroundColor: "#E9F1F2", width: LEFT_COL_W, minWidth: LEFT_COL_W }}
                                        >
                                            必要人数
                                        </th>
                                        <th
                                            className="border-b border-r border-[#D9D9D9] px-1 py-1 text-center font-medium text-[#1A1A1A] sticky z-30"
                                            style={{ backgroundColor: "#E9F1F2", width: HOUR_COL_W, minWidth: HOUR_COL_W, left: LEFT_COL_W }}
                                        />
                                        {Array.from({ length: visibleSlotCount }, (_, idx) => {
                                            const i = visibleSlotStart + idx;
                                            const isHourBoundary = i % SLOTS_PER_HOUR === 0;
                                            const count = headcount[i];
                                            return (
                                                <th
                                                    key={`hc-${i}`}
                                                    className={clsx(
                                                        "border-b border-b-[#D9D9D9] border-l border-l-[#D9D9D9] px-0 py-1 text-center text-xs font-normal text-[#1A1A1A]",
                                                    )}
                                                    style={{ backgroundColor: "#E9F1F2", width: SLOT_W, minWidth: SLOT_W, ...(!isHourBoundary ? { borderLeftStyle: "dashed" as const } : {}) }}
                                                >
                                                    {count > 0 ? count : ""}
                                                </th>
                                            );
                                        })}
                                    </tr>

                                    {/* ヘッダー②: 時刻 */}
                                    <tr style={{ backgroundColor: "#E9F1F2" }}>
                                        <th
                                            className="border-b border-r border-[#D9D9D9] px-2 py-1 text-center font-medium text-[#1A1A1A] whitespace-nowrap sticky left-0 z-30"
                                            style={{ backgroundColor: "#E9F1F2", width: LEFT_COL_W, minWidth: LEFT_COL_W }}
                                        >
                                            作業時間
                                        </th>
                                        <th
                                            className="border-b border-r border-[#D9D9D9] px-1 py-1 text-center font-medium text-[#1A1A1A] sticky z-30"
                                            style={{ backgroundColor: "#E9F1F2", width: HOUR_COL_W, minWidth: HOUR_COL_W, left: LEFT_COL_W }}
                                        />
                                        {visibleHours.map((h) => (
                                            <th
                                                key={`hr-${h}`}
                                                colSpan={SLOTS_PER_HOUR}
                                                className="border-b border-l border-[#D9D9D9] px-0 py-1 text-center text-sm font-medium text-[#1A1A1A] whitespace-nowrap"
                                                style={{ backgroundColor: "#E9F1F2" }}
                                            >
                                                {h}時
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody ref={tbodyRef}>
                                    {rows.map((row) => (
                                        <tr key={row.id}>
                                            <td
                                                className="border-b border-b-[#D9D9D9] border-r border-[#D9D9D9] px-2 py-1 text-center whitespace-nowrap text-[#1A1A1A] sticky left-0 z-10 bg-white"
                                                style={{ width: LEFT_COL_W, minWidth: LEFT_COL_W }}
                                            >
                                                {isFilledRow(row)
                                                    ? `${formatTime(row.startHour ?? 0, row.startMin ?? 0)}〜${formatTime(row.endHour ?? 0, row.endMin ?? 0)}`
                                                    : ""}
                                            </td>
                                            <td
                                                className="border-b border-b-[#D9D9D9] border-r border-[#D9D9D9] px-1 py-1 text-center text-[#1A1A1A] sticky z-10 bg-white"
                                                style={{ width: HOUR_COL_W, minWidth: HOUR_COL_W, left: LEFT_COL_W }}
                                            >
                                                {isFilledRow(row) ? calcTotalHours(row) : ""}
                                            </td>
                                            <TimelineCells
                                                row={row}
                                                slotStart={visibleSlotStart}
                                                slotEnd={visibleSlotEnd}
                                                drag={drag}
                                                onCellMouseDown={handleCellMouseDown}
                                                onCellMouseEnter={handleCellMouseEnter}
                                            />
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            )}
                        </div>
                    </div>

                    {/* 1行追加ボタン */}
                    <div className="flex items-center justify-center mt-2">
                        <button
                            type="button"
                            onClick={handleAddRow}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            ＋ 1行追加
                        </button>
                    </div>

                    {/* 保存ボタン（画面右下） */}
                    <div className="mt-auto flex justify-end gap-2 pt-4">
                        <Button variant="outline" size="md" onClick={handleCancel} disabled={!dirtyWeekdays.has(currentWeekday) || saving} className="px-8">
                            キャンセル
                        </Button>
                        <Button variant="primary" size="md" onClick={handleSave} disabled={!isDirty || saving} className="px-8">
                            {saving ? "登録中..." : "登録する"}
                        </Button>
                    </div>
                </div>
            </div>
    );
};

export default RequiredManHoursPage;
